import { Button, Tooltip, Checkbox } from 'antd';
import { debounce, pick, startsWith } from 'lodash';
import isEqual from 'lodash/isEqual';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import Icon, { DownOutlined, LoadingOutlined } from '@ant-design/icons';
import ResponsiveStatusTag from '@components/common/DimensionTable/components/ResponsiveStatusTag';
import AddSvg from '@icons/mapping-panel/add-rule-icon.svg';
import DeleteSvg from '@icons/mapping-panel/delete-icon.svg';
import { useFetchAttributeTypes, useMappedEventNames } from '@src/emp-3.0/api/queryHooks';
import {
  INameSelectWithTabs,
  NameSelectWithTabs,
} from '@src/emp-3.0/components/custom-dimension/component/NamedSelectWithExtras';
import { ICustomDimensionRuleBuilder } from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/components/RuleBuilder/typed';
import { useCustomDimensionPanelStore } from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/store';
import { RuleBuilder } from '@src/emp-3.0/components/custom-dimension/custom-dimension-panel/styled';
import {
  addTextPrefix,
  isEventAttributeType,
  isGlobalAttributeType,
  transformAttributeType,
  trimStartPrefix,
} from '@src/emp-3.0/components/custom-dimension/utils';
import { EAttributeType, ECategoryType, TAttributeType } from '@src/emp-3.0/types';
import { toLowerCase } from '@src/emp-3.0/utils';
import commonSelector from '@store/common/selectors';

import {
  GLOBAL_TAG_TAB_KEY,
  EVENT_TAG_TAB_KEY,
  UNMAPPED_EVENT_TAB_KEY,
  MAPPED_EVENT_TAB_KEY,
  EMPTY_ARRAY,
  GLOBAL_KEYS_PREFIX,
  EVENT_KEYS_PREFIX,
} from '../../constant';
import { AttributeContainer, EventName, EventNamesContainer, RulesWrapper } from './styled';
import AddEllipsisWithTooltip from '@components/common/EllipsisTooltip/AddEllipsisWithTooltip';
import { useCustomDimensionStore } from '@src/emp-3.0/components/Dimensions/store';
import {
  ALL_APPLICABLE_MAPPED_EVENTS_TYPE,
  SPECIFIC_MAPPED_EVENT_TYPE,
} from '@src/emp-3.0/constants/dimensions';
import { isUndefined } from '@src/emp-3.0/utils/checks';
import { useDimensionWorkflow } from '@hooks';
import EditableSelect from '@src/emp-3.0/components/custom-dimension/component/NamedSelectWithExtras/EditableSelect';

const defaultSelectEventTypeOptionsInNewWorkflow = [
  {
    label: (
      <Tooltip title="The selected tag key will be exposed to all applicable mapped events that carry the tag key.">
        {ALL_APPLICABLE_MAPPED_EVENTS_TYPE}
      </Tooltip>
    ),
    value: ALL_APPLICABLE_MAPPED_EVENTS_TYPE,
  },
  {
    label: SPECIFIC_MAPPED_EVENT_TYPE,
    value: SPECIFIC_MAPPED_EVENT_TYPE,
  },
];

export const generateTagKeyTabItems = ({ globalTagKeyCount = 0, eventTagKeyCount = 0 }) => {
  return [
    {
      label: <Tooltip title="Tag key shared by all events">Global({globalTagKeyCount})</Tooltip>,
      key: GLOBAL_TAG_TAB_KEY,
    },
    {
      label: (
        <Tooltip title="Tag key specific to certain events only">
          Event Specific({eventTagKeyCount})
        </Tooltip>
      ),
      key: EVENT_TAG_TAB_KEY,
    },
  ];
};

export const suffixIcon = (isLoading?: boolean, isGlobalAttribute?: boolean) => {
  if (isLoading) return <LoadingOutlined />;
  if (isUndefined(isGlobalAttribute)) return <DownOutlined />;
  return (
    <div>
      <span style={{ color: '#949494', fontSize: 12, marginRight: 8 }}>
        {isGlobalAttribute ? 'Global' : 'Event Specific'}
      </span>
      <DownOutlined />
    </div>
  );
};

export const checkKeyType = ({
  value,
  eventTagTabsKey,
  attributeTypesData,
}: {
  value: string;
  attributeTypesData?: { global_keys: any[]; event_keys: any[] };
  eventTagTabsKey: string;
}) => {
  const { global_keys = [], event_keys = [] } = attributeTypesData || {};
  const isGlobalKey = global_keys.find((key) => key === value);
  const isEventKey = event_keys.find((key) => key === value);
  const isEventAttributeType =
    (!isGlobalKey && isEventKey) || (eventTagTabsKey === EVENT_TAG_TAB_KEY && isEventKey);
  if (isEventAttributeType) {
    return EAttributeType.Event;
  } else {
    return EAttributeType.Global;
  }
};

const generateSelectMappedEventTabItems = ({ mappedEventCount = 0, unmappedEventCount = 0 }) => {
  return [
    {
      label: `Mapped Event(${mappedEventCount})`,
      key: MAPPED_EVENT_TAB_KEY,
    },
    { label: `Raw Event(${unmappedEventCount})`, key: UNMAPPED_EVENT_TAB_KEY },
  ];
};

const SelectEventName: React.FC<
  INameSelectWithTabs & {
    eventNames: string[];
    data: {
      mapped_event_names: string[];
      raw_event_names: string[];
    };
  }
> = ({ eventNames = [], data, onTabsItemClick, selectProps, ...restProps }) => {
  const [eventNamesTabsKey, setEventNamesTabsKey] = useState<string>(MAPPED_EVENT_TAB_KEY);
  const [searchText, setSearchText] = useState<string>('');

  const { mappedEventNamesOptions, rawEventNamesOptions } = useMemo(() => {
    const { mapped_event_names = [], raw_event_names = [] } = data || {};
    const mappedEventNamesOptions = mapped_event_names.map((name) => {
      const isPredefinedConvivaMappedEvent = startsWith(name, 'conviva_');
      return {
        label: isPredefinedConvivaMappedEvent ? (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <AddEllipsisWithTooltip text={name || ''} dataIndex="0" />
            <ResponsiveStatusTag text={'Conviva Predefined'} />
          </div>
        ) : (
          name
        ),
        value: name,
        disabled: eventNames.includes(name),
      };
    });
    const rawEventNamesOptions = raw_event_names.map((name) => ({
      label: name,
      value: name,
      disabled: true,
    }));
    return {
      mappedEventNamesOptions,
      rawEventNamesOptions,
    };
  }, [data, eventNames]);

  const eventNamesOptions = useMemo(() => {
    return eventNamesTabsKey === MAPPED_EVENT_TAB_KEY
      ? mappedEventNamesOptions
      : rawEventNamesOptions;
  }, [eventNamesTabsKey, mappedEventNamesOptions, rawEventNamesOptions]);

  const mappedEventCount = useMemo(() => {
    if (!searchText) return mappedEventNamesOptions.length;
    return mappedEventNamesOptions.filter((option) =>
      toLowerCase(option?.value).includes(toLowerCase(searchText)),
    ).length;
  }, [mappedEventNamesOptions, searchText]);

  const unmappedEventCount = useMemo(() => {
    if (!searchText) return rawEventNamesOptions.length;
    return rawEventNamesOptions.filter((option) =>
      toLowerCase(option?.value).includes(toLowerCase(searchText)),
    ).length;
  }, [rawEventNamesOptions, searchText]);

  return (
    <NameSelectWithTabs
      activeTabKey={eventNamesTabsKey}
      items={generateSelectMappedEventTabItems({
        mappedEventCount,
        unmappedEventCount,
      })}
      onTabsItemClick={(value) => {
        setEventNamesTabsKey(value.key);
        onTabsItemClick?.(value);
      }}
      dropdownRenderProps={{
        onMouseDown: (e) => {
          e.preventDefault();
          e.stopPropagation();
        },
      }}
      selectProps={{
        options: eventNamesOptions,
        showSearch: true,
        filterOption: (input, option) => toLowerCase(option?.value).includes(toLowerCase(input)),
        ...selectProps,
        onChange: (value) => {
          setSearchText('');
          selectProps?.onChange?.(value);
        },
        onSearch: (value) => {
          setSearchText(value);
          selectProps?.onSearch?.(value);
        },
        onBlur: () => {
          if (searchText) {
            selectProps?.onChange?.(searchText);
            setSearchText('');
          }
        },
      }}
      {...restProps}
    />
  );
};

const CustomDimensionRuleBuilder: React.FC<ICustomDimensionRuleBuilder> = ({
  disabled = false,
  eventTag: propsEventTag = '',
  eventNames: propsEventNames = EMPTY_ARRAY,
  attributeType: propsAttributeType,
  selectEventType: propsSelectEventType = ALL_APPLICABLE_MAPPED_EVENTS_TYPE,
  promoteAsGlobal,
  dimensionIdentifier,
  onEventTagChange,
  onEventNamesChange,
  onAttributeTypeChange,
  onCategoryChange,
  onSelectEventTypeChange,
  onPromoteAsGlobalChange,
  errorField = '',
}) => {
  const { interval } = useSelector(commonSelector);
  const isNewWorkflow = useDimensionWorkflow();

  const {
    customDimensionPayload,
    setCustomDimensionPayload,
    attributeFiltersPayload,
    setAttributeFiltersPayload,
    resetCustomDimensionStore,
  } = useCustomDimensionPanelStore((state) => state);
  const {
    customDimensionPayload: customDimensionPayloadInNewWorkflow,
    setCustomDimensionPayload: setCustomDimensionPayloadInNewWorkflow,
    attributeFiltersPayload: attributeFiltersPayloadInNewWorkflow,
    setAttributeFiltersPayload: setAttributeFiltersPayloadInNewWorkflow,
    resetCustomDimensionStore: resetCustomDimensionStoreInNewWorkflow,
  } = useCustomDimensionStore((state) => state);

  const isUpdateDimension = isNewWorkflow
    ? 'custom_dimension_id' in customDimensionPayloadInNewWorkflow
    : 'custom_dimension_id' in customDimensionPayload;

  const intervalStr = `${interval?.from}/${interval?.to}`;

  const fetchAttributeFiltersPayload = useMemo(() => {
    return isNewWorkflow ? attributeFiltersPayloadInNewWorkflow : attributeFiltersPayload;
  }, [attributeFiltersPayload, attributeFiltersPayloadInNewWorkflow, isNewWorkflow]);

  const [eventTag, setEventTag] = useState<string>(
    !propsEventTag
      ? propsEventTag
      : addTextPrefix(propsEventTag, transformAttributeType(propsAttributeType)),
  );
  const [eventNames, setEventNames] = useState<string[]>(propsEventNames);
  const [attributeType, setAttributeType] = useState<undefined | TAttributeType>(
    propsAttributeType,
  );
  const [eventTagTabsKey, setEventTagTabsKey] = useState<string>(GLOBAL_TAG_TAB_KEY);
  const [searchText, setSearchText] = useState<string>('');
  const [selectEventType, setSelectEventType] = useState<string>(propsSelectEventType);
  const [cacheAttributeTypesData, setCacheAttributeTypesData] = useState<{
    global_keys: any[];
    event_keys: any[];
  }>();
  const [freeEdit, setFreeEdit] = useState<boolean>(false);

  const { data: mappedEventNamesData, isFetching: isGetMappedEventNamesLoading } =
    useMappedEventNames({
      interval: intervalStr,
      event_tag: trimStartPrefix(eventTag, transformAttributeType(propsAttributeType)),
    });

  // When click on' New Custom Dimension' kebab to enter, if the interface returns empty data, the data is reset.
  const handleSuccess = (data) => {
    const { global_keys, event_keys } = data || {};
    if (
      !isUpdateDimension &&
      global_keys?.length === 0 &&
      event_keys?.length === 0 &&
      fetchAttributeFiltersPayload
    ) {
      isNewWorkflow ? resetCustomDimensionStoreInNewWorkflow() : resetCustomDimensionStore();
    }
  };

  // If an interface error occurs, reset the data.
  const handleError = (error) => {
    if (!isUpdateDimension) {
      isNewWorkflow ? resetCustomDimensionStoreInNewWorkflow() : resetCustomDimensionStore();
    }
  };

  const { data: exactAttributeTypesData, isFetching: isGetExactAttributeTypesLoading } =
    useFetchAttributeTypes(
      { filters: fetchAttributeFiltersPayload },
      {
        enabled: !!fetchAttributeFiltersPayload,
        onSuccess: handleSuccess,
        onError: handleError,
      },
    );
  const { data: attributeTypesData, isFetching: isGetAttributeTypesLoading } =
    useFetchAttributeTypes(
      { value: searchText },
      {
        enabled: !fetchAttributeFiltersPayload || searchText !== '',
        onError: handleError,
      },
    );

  const isLoading = useMemo(
    () => isGetAttributeTypesLoading || isGetExactAttributeTypesLoading,
    [isGetAttributeTypesLoading, isGetExactAttributeTypesLoading],
  );
  const isGlobalAttribute = useMemo(
    () => (isUndefined(attributeType) ? undefined : isGlobalAttributeType(attributeType)),
    [attributeType],
  );

  const curAttributeTypesData = useMemo<{
    global_keys: any[];
    event_keys: any[];
  }>(() => {
    let data = attributeTypesData;
    if (fetchAttributeFiltersPayload && searchText === '') {
      data = exactAttributeTypesData;
    } else if (searchText === '') {
      data = cacheAttributeTypesData;
    } else {
      data = attributeTypesData;
    }
    return data;
  }, [
    attributeTypesData,
    cacheAttributeTypesData,
    exactAttributeTypesData,
    fetchAttributeFiltersPayload,
    searchText,
  ]);

  const { globalTagsOptions, eventTagsOptions } = useMemo(() => {
    const { global_keys = [], event_keys = [] } = curAttributeTypesData || {};
    const globalTagsOptions = global_keys.map((tag) => ({
      label: tag,
      value: addTextPrefix(tag, GLOBAL_KEYS_PREFIX),
      global: true,
    }));
    const eventTagsOptions = event_keys.map((tag) => ({
      label: tag,
      value: addTextPrefix(tag, EVENT_KEYS_PREFIX),
    }));
    return {
      globalTagsOptions,
      eventTagsOptions,
    };
  }, [curAttributeTypesData]);

  useEffect(() => {
    if (!globalTagsOptions.length && !eventTagsOptions.length && searchText) {
      setAttributeType(undefined);
      onCategoryChange?.('');
    }
  }, [globalTagsOptions, eventTagsOptions, searchText, onCategoryChange]);

  const tagKeyValue = useMemo(() => {
    const findOption = [...globalTagsOptions, ...eventTagsOptions].find(
      (item) => item.value === eventTag,
    );
    if (findOption) return findOption;
    return propsEventTag;
  }, [eventTag, propsEventTag, globalTagsOptions, eventTagsOptions]);

  // When click on' New Custom Dimension' kebab to enter,
  // the default attribute_type may be inaccurate and needs to be obtained from the interface.
  const getExactAttributeType = (data, key) => {
    const { global_keys = [], event_keys = [] } = data || {};
    const isGlobalKey = global_keys.find((global_key) => global_key === key);
    const isEventKey = event_keys.find((event_key) => event_key === key);

    if (isUndefined(isGlobalKey) && isUndefined(isEventKey)) {
      return {
        attribute_type: attributeType,
        category: isUndefined(isGlobalAttribute)
          ? undefined
          : isGlobalAttribute
          ? ECategoryType.GLOBAL_TAGS
          : ECategoryType.EVENT_TAGS,
      };
    }

    let attribute_type = EAttributeType.Global,
      category = ECategoryType.GLOBAL_TAGS;
    if (isEventKey && !isGlobalKey) {
      attribute_type = EAttributeType.Event;
      category = ECategoryType.EVENT_TAGS;
    }
    return {
      attribute_type,
      category,
    };
  };

  useEffect(() => {
    setFreeEdit(false);
    setAttributeType(propsAttributeType); // don't add it in dependency, keep it to original value
  }, [disabled]);

  useEffect(() => {
    if (typeof tagKeyValue === 'object') {
      setFreeEdit(false);
    }
  }, [tagKeyValue]);

  useEffect(() => {
    // Cache the dropdown data corresponding to the current value
    attributeTypesData &&
      setCacheAttributeTypesData((prevState) => {
        if (prevState) return prevState;
        return attributeTypesData;
      });
  }, [attributeTypesData]);

  // update attribute type
  useEffect(() => {
    setAttributeType(propsAttributeType);
  }, [propsAttributeType]);

  useEffect(() => {
    const exactAttributeType = getExactAttributeType(exactAttributeTypesData, propsEventTag);
    if (fetchAttributeFiltersPayload) {
      const payload = {
        category: exactAttributeType.category,
        attribute_type: exactAttributeType.attribute_type,
      };
      isNewWorkflow
        ? setCustomDimensionPayloadInNewWorkflow(payload)
        : setCustomDimensionPayload(payload);
    }
  }, [isNewWorkflow, propsEventTag, exactAttributeTypesData, fetchAttributeFiltersPayload]);

  useEffect(() => {
    // set init tab key
    if (isEventAttributeType(propsAttributeType)) {
      setEventTagTabsKey(EVENT_TAG_TAB_KEY);
    } else {
      setEventTagTabsKey(GLOBAL_TAG_TAB_KEY);
    }
  }, [propsAttributeType]);

  // update event tag
  useEffect(() => {
    const transformPrefix = isUndefined(attributeType)
      ? ''
      : attributeType === EAttributeType.Event
      ? EVENT_KEYS_PREFIX
      : GLOBAL_KEYS_PREFIX;
    let prefix = '';

    const targetOptions =
      transformPrefix === EVENT_KEYS_PREFIX ? eventTagsOptions : globalTagsOptions;
    const findOption = targetOptions.find((option) => option.label === propsEventTag);

    if (findOption) {
      prefix = transformPrefix;
    }

    const eventTagValue = !propsEventTag ? propsEventTag : addTextPrefix(propsEventTag, prefix);
    setEventTag(eventTagValue);
  }, [attributeType, propsEventTag, globalTagsOptions, eventTagsOptions]);

  useEffect(() => {
    // update select event type(global / event)
    setSelectEventType(propsSelectEventType);
    // set init event names
    if (propsEventNames.length > 0) {
      setEventNames([...propsEventNames]);
    } else {
      setEventNames(propsSelectEventType === SPECIFIC_MAPPED_EVENT_TYPE ? [''] : []);
    }
  }, [propsEventNames, propsSelectEventType]);

  const handleAttributeTypeChange = (prefix: string) => {
    const type = prefix === EVENT_KEYS_PREFIX ? EAttributeType.Event : EAttributeType.Global;
    setAttributeType(type);
    onAttributeTypeChange?.(type);
  };

  const handleCategoryChange = (prefix: string) => {
    let category =
      prefix === EVENT_KEYS_PREFIX ? ECategoryType.EVENT_TAGS : ECategoryType.GLOBAL_TAGS;
    category = promoteAsGlobal ? ECategoryType.GLOBAL_TAGS : category; // keep category as global when promoted as global
    onCategoryChange?.(category);
  };

  const handleEventNamesChange = (value) => {
    setEventNames(value);
    onEventNamesChange?.(value);
  };

  const handleEventTagChange = (value: string, skipType?: boolean) => {
    if (searchText !== '') {
      isNewWorkflow ? setAttributeFiltersPayloadInNewWorkflow() : setAttributeFiltersPayload();
    }
    setCacheAttributeTypesData(attributeTypesData);
    setSearchText('');
    let prefix;
    if (startsWith(value, GLOBAL_KEYS_PREFIX)) {
      prefix = GLOBAL_KEYS_PREFIX;
    }
    if (startsWith(value, EVENT_KEYS_PREFIX)) {
      prefix = EVENT_KEYS_PREFIX;
    }
    const actualEventTag = trimStartPrefix(value, prefix);
    setEventTag(value);
    onEventTagChange?.(actualEventTag);
    if (!skipType) {
      handleAttributeTypeChange(prefix);
      handleCategoryChange(prefix);
    }
  };

  const handleTypeChange = (type: TAttributeType) => {
    setAttributeType(type);
    onAttributeTypeChange?.(type);
    const category = isGlobalAttributeType(type)
      ? ECategoryType.GLOBAL_TAGS
      : ECategoryType.EVENT_TAGS;
    onCategoryChange?.(category);
  };

  const handleUpdateEventNamesChange = (value: string, index: number) => {
    const copiedArray: string[] = [...eventNames];
    copiedArray[index] = value;
    handleEventNamesChange(copiedArray);
  };

  const handleDeleteEventNamesClick = (indexToRemove) => {
    let copiedArray: string[] = [...eventNames];
    copiedArray.splice(indexToRemove, 1);
    if (copiedArray.length === 0) {
      copiedArray = [''];
    }
    handleEventNamesChange(copiedArray);
  };

  const handleAddEventNamesClick = () => {
    const copiedArray: string[] = [...eventNames];
    copiedArray.push('');
    handleEventNamesChange(copiedArray);
  };

  const handleSelectEventTypeChange = (value) => {
    setSelectEventType(value);
    onSelectEventTypeChange?.(value);
  };

  const handlePromoteAsGlobalChange = (e) => {
    const isPromotedAsGlobal = e.target.checked ? 1 : 0;
    onPromoteAsGlobalChange?.(isPromotedAsGlobal);
    if (isPromotedAsGlobal) {
      onCategoryChange?.(ECategoryType.GLOBAL_TAGS);
      setSelectEventType(ALL_APPLICABLE_MAPPED_EVENTS_TYPE);
      onSelectEventTypeChange?.(ALL_APPLICABLE_MAPPED_EVENTS_TYPE);
    } else {
      const category = isGlobalAttribute ? ECategoryType.GLOBAL_TAGS : ECategoryType.EVENT_TAGS;
      onCategoryChange?.(category);
    }
  };
  useEffect(() => {
    if (isGlobalAttribute) {
      onPromoteAsGlobalChange?.(0);
    }
  }, [isGlobalAttribute, onPromoteAsGlobalChange]);

  const extraNodes = (index: number) => {
    const deleteButton = (
      <Icon
        key="select-extra-delete"
        component={DeleteSvg}
        onClick={() => handleDeleteEventNamesClick(index)}
        style={{
          color: '#949494',
          width: 20,
          height: '100%',
          display: 'inline-flex',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
      />
    );

    const addButton = (
      <Button
        key="select-extra-add"
        type="link"
        style={{ padding: 0 }}
        onClick={handleAddEventNamesClick}
      >
        <Icon component={AddSvg} />
        {!isNewWorkflow && <span>Add</span>}
      </Button>
    );

    if (disabled) {
      return [];
    } else if (index === eventNames.length - 1) {
      return [deleteButton, addButton];
    } else {
      return [
        deleteButton,
        <span key="select-extra-placeholder" style={{ display: 'inline-block', width: '16px' }} />,
      ];
    }
  };
  const isTraceId = useMemo(() => {
    return dimensionIdentifier === 'trace_id';
  }, [dimensionIdentifier]);

  return (
    <AttributeContainer active={!disabled}>
      <EventName>
        <div style={{ marginRight: 8, flexShrink: 0 }}>Tag Key * : </div>
        <EditableSelect
          showSearch
          allowClear
          style={{ maxWidth: 400, width: '100%' }}
          dropdownAlign={{ overflow: { adjustY: 0 } }}
          isGlobalAttribute={isGlobalAttribute}
          freeEdit={freeEdit}
          disabled={disabled}
          loading={isLoading}
          globalTagsOptions={globalTagsOptions}
          eventTagsOptions={eventTagsOptions}
          status={errorField === 'attribute_name' ? 'error' : undefined}
          value={tagKeyValue || undefined}
          onSearch={debounce((value) => {
            setSearchText(value);
          }, 800)}
          onChange={(value) => {
            handleEventTagChange(value);
            setFreeEdit(false);
          }}
          onChangeType={(value) => handleTypeChange(value)}
          onBlur={() => {
            if (searchText) {
              handleEventTagChange(searchText, true);
              setFreeEdit(true);
            }
          }}
        />
        {!isGlobalAttribute && !isTraceId && (
          <Tooltip
            title={`Select this if the tag should be treated as global across all events, even though it was collected as an event-specific tag.\nLeave unchecked to keep the original event-only behavior.`}
            overlayStyle={{ maxWidth: '400px' }}
          >
            <Checkbox
              disabled={disabled}
              checked={Boolean(promoteAsGlobal === 1)}
              onChange={handlePromoteAsGlobalChange}
            >
              <span style={{ whiteSpace: 'nowrap' }}>Promote as Global Dimension</span>
            </Checkbox>
          </Tooltip>
        )}
      </EventName>
      <RuleBuilder>
        <EventNamesContainer>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ marginRight: 8, flexShrink: 0 }}>Select :</div>
            <NameSelectWithTabs
              selectProps={{
                disabled:
                  disabled ||
                  isGlobalAttribute ||
                  isUndefined(attributeType) ||
                  promoteAsGlobal === 1,
                value: selectEventType,
                options: defaultSelectEventTypeOptionsInNewWorkflow,
                onChange: handleSelectEventTypeChange,
              }}
            />
          </div>
          {!isUndefined(isGlobalAttribute) &&
            !isGlobalAttribute &&
            !promoteAsGlobal &&
            selectEventType === SPECIFIC_MAPPED_EVENT_TYPE && (
              <RulesWrapper>
                {eventNames.map((eventName, index) => {
                  return (
                    <SelectEventName
                      key={`event_name_${eventName}_${index}`}
                      eventNames={eventNames}
                      data={mappedEventNamesData}
                      extraNodes={extraNodes(index)}
                      selectProps={{
                        disabled,
                        loading: isGetMappedEventNamesLoading,
                        value: eventName || undefined,
                        onChange: (value) => handleUpdateEventNamesChange(value, index),
                        style: { maxWidth: 605, width: '100%' },
                        optionLabelProp: 'value',
                        status: errorField === 'mapped_event_names' ? 'error' : undefined,
                      }}
                    />
                  );
                })}
              </RulesWrapper>
            )}
        </EventNamesContainer>
      </RuleBuilder>
    </AttributeContainer>
  );
};

export default React.memo(CustomDimensionRuleBuilder, (prevProps, nextProps) => {
  return isEqual(
    pick(prevProps, [
      'disabled',
      'eventTag',
      'eventNames',
      'attributeType',
      'selectEventType',
      'promoteAsGlobal',
      'errorField',
      'dimensionIdentifier',
    ]),
    pick(nextProps, [
      'disabled',
      'eventTag',
      'eventNames',
      'attributeType',
      'selectEventType',
      'promoteAsGlobal',
      'errorField',
      'dimensionIdentifier',
    ]),
  );
});
